import type { ReactElement } from "react";

import { useSelector } from "react-redux";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";

import BaseActivatedPanel from "../../../../table-actions-panel/components/BaseActivatedPanel/BaseActivatedPanel";
import type { BaseActivatedPanelProps } from "../../../../table-actions-panel/components/BaseActivatedPanel/types";
import ExportInfoMessage from "../../../../table-actions-panel/components/ExportInfoMessage/ExportInfoMessage";
import SelectionDDItem from "../../../../table-actions-panel/components/SelectionDDItem/SelectionDDItem";
import ManualSelectionItem from "../../../../table-actions-panel/models/selection-dropdown-item/ManualSelectionItem/ManualSelectionItem";
import { isExportFlowActive } from "../../../helpers/exportFlow";
import { useExportPanelFlow } from "../../../hooks/useExportPanelFlow";
import { selectCrmExportPairs, selectExportFlowPhase, selectExportVariant } from "../../../store/selectors";
import type { ContactSearchEntry } from "../../../types/common";
import type { ExportToExcelFn } from "../../../types/exportFlow";
import { RevealBeforeExportModal } from "../../RevealBeforeExportModal/RevealBeforeExportModal";
import { ExportButton } from "../ExportButton/ExportButton";
import ExportContactsToCrmContainer from "../ExportContactsToCrm/ExportContactsToCrmContainer";
import InnerLoader from "../InnerLoader/InnerLoader";

import {
    StyledCheckbox,
    StyledExcludeExportedSection,
    StyledInfoIcon,
    StyledPanel,
    StyledSelectionDropdown,
    StyledShareIcon,
    StyledTitle,
} from "./styles";

export type ActiveContactsExportPanelPropsBase = BaseActivatedPanelProps & {
    countries: readonly number[];
    onExportToExcel: ExportToExcelFn;
    onRefetchData?: () => void;
};

export type ActiveContactsExportPanelProps = ActiveContactsExportPanelPropsBase & {
    contacts: ContactSearchEntry[];
    totalCount: number;
    isLoading: boolean;
    onFetchTopNContacts: (n: number) => Promise<ContactSearchEntry[]>;
};

export const ActiveContactsExportPanel = ({
    contacts,
    totalCount,
    isLoading,
    countries,
    onDeactivate,
    onExportToExcel,
    onFetchTopNContacts,
    onRefetchData,
}: ActiveContactsExportPanelProps): ReactElement => {
    const phase = useSelector(selectExportFlowPhase);
    const exportVariant = useSelector(selectExportVariant);
    const crmExportPairs = useSelector(selectCrmExportPairs);

    const isActive = isExportFlowActive(phase);
    const isFetchingContacts = phase.type === "fetchingContacts";
    const isExcelVariant = "variant" in phase && phase.variant === "excel";
    const hasCrmModal = phase.type === "matchingCrmContacts" || phase.type === "previewingCrmContacts";

    const {
        isConfirmModalOpen,
        isExportedExcluded,
        activeSelectionItem,
        dropdownItems,
        selectedContactsIds,
        contactsToRevealCount,
        revealedInSelectionCount,
        remainingCredits,
        isSubmitButtonDisabled,
        clearAndClosePanel,
        onSelectionChange,
        onExcludeExportedToggle,
        onSubmit,
        onConfirmRevealAndExport,
        onCancelRevealAndExport,
        onConfirmCrmPreviewExport,
        onCancelCrmPreview,
        onCancelCrmLoading,
    } = useExportPanelFlow({
        contacts,
        totalCount,
        isLoading,
        countries,
        onDeactivate,
        onExportToExcel,
        onFetchTopNContacts,
        onRefetchData,
    });

    return (
        <>
            <BaseActivatedPanel
                onDeactivate={clearAndClosePanel}
                hideCloseButton={isActive}
            >
                {isLoading || (isActive && !hasCrmModal) ? (
                    <InnerLoader
                        text={translate(
                            isLoading || isFetchingContacts
                                ? "si.contacts-actions-panel.loading.text"
                                : isExcelVariant
                                  ? "si.contacts-actions-panel.exporting-to-excel.text"
                                  : "si.contacts-actions-panel.exporting-to-crm.text",
                        )}
                    />
                ) : (
                    <StyledPanel>
                        <StyledShareIcon>
                            <SWReactIcons
                                iconName="share"
                                size="xs"
                            />
                        </StyledShareIcon>
                        <StyledTitle data-automation="contacts-actions-panel.export-label">
                            {translate("si.advanced_search.contacts_search.action.export.label")}:
                        </StyledTitle>
                        <StyledExcludeExportedSection>
                            <StyledCheckbox
                                selected={isExportedExcluded}
                                color={colorsPalettes.carbon["0"]}
                                onClick={onExcludeExportedToggle}
                                label={translate("si.contacts_search.action.export.checkbox.label")}
                            />
                            <Tooltip content={translate("si.contacts_search.action.export.checkbox.tooltip")}>
                                <StyledInfoIcon>
                                    <SWReactIcons
                                        iconName="info"
                                        size="xs"
                                    />
                                </StyledInfoIcon>
                            </Tooltip>
                        </StyledExcludeExportedSection>
                        <StyledSelectionDropdown
                            buttonText={activeSelectionItem.getSelectedTranslation()}
                            onSelect={onSelectionChange}
                        >
                            {dropdownItems
                                .map((item) => (
                                    <SelectionDDItem
                                        key={item.id}
                                        item={item}
                                        isSelected={activeSelectionItem.id === item.id}
                                    />
                                ))
                                .concat(
                                    <SelectionDDItem
                                        key={ManualSelectionItem.ID}
                                        item={new ManualSelectionItem(selectedContactsIds.length)}
                                        isSelected={activeSelectionItem.id === ManualSelectionItem.ID}
                                    />,
                                )}
                        </StyledSelectionDropdown>
                        <ExportButton
                            isLoading={isExcelVariant}
                            isDisabled={isSubmitButtonDisabled}
                            onClick={onSubmit}
                        />
                        {!isConfirmModalOpen && exportVariant === "crm" && crmExportPairs.length === 0 ? (
                            <ExportInfoMessage>{translate("si.multi_selector.crm_export_info")}</ExportInfoMessage>
                        ) : null}
                    </StyledPanel>
                )}
            </BaseActivatedPanel>
            {exportVariant === "crm" && crmExportPairs.length > 0 ? (
                <ExportContactsToCrmContainer
                    onConfirmExport={onConfirmCrmPreviewExport}
                    onCancelPreview={onCancelCrmPreview}
                    onCancelLoading={onCancelCrmLoading}
                />
            ) : null}
            <RevealBeforeExportModal
                isOpen={isConfirmModalOpen}
                unrevealedCount={contactsToRevealCount}
                revealedCount={revealedInSelectionCount}
                remainingCredits={remainingCredits}
                onConfirm={onConfirmRevealAndExport}
                onClose={onCancelRevealAndExport}
            />
        </>
    );
};
